// Number rules in the browser: the dialer, call-back, transfer and Add refuse
// these numbers at once with a clear message. The server checks them too (api +
// Asterisk); this is only for the instant message. The list comes from
// /v1/softphone-config (numberRules.blockedPrefixes); the default is used until then.
//
// Everything below is a copy of app/backend/src/number-rules.ts (a web test
// compares the two): change both together.

/** Prefixes (after the leading 1) blocked by default. setup.sh writes the same list into app.env. */
export const DEFAULT_BLOCKED_PREFIXES: readonly string[] = [
  // premium rate
  '900', '976',
  // Caribbean countries (+1 but international rates)
  '242', '246', '264', '268', '284', '345', '441', '473', '649', '658', '664', '721', '758', '767', '784', '809',
  '829', '849', '868', '869', '876',
  // US territories (owner, session 16: blocked)
  '340', '670', '671', '684', '787', '939',
  // N11 service codes used as an area code
  '211', '311', '411', '511', '611', '711', '811', '911',
  // non-geographic
  '500', '533', '544', '566', '577', '588', '700',
];

/** The dialplan checks prefixes of these lengths (CP_BLK_<first 3..7 digits after the 1>). */
export const PREFIX_MIN = 3;
export const PREFIX_MAX = 7;

const PREFIX = /^[0-9]{3,7}$/;

/** "900, 976,876" -> ['900','976','876'] (sorted, no duplicates); invalid entries are dropped. Empty -> the default list. */
export function parseBlockedPrefixes(raw: string | null | undefined): string[] {
  const items = (raw ?? '')
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => PREFIX.test(s));
  return [...new Set(items.length ? items : DEFAULT_BLOCKED_PREFIXES)].sort();
}

export type NumberProblem = 'invalid' | 'emergency' | 'service' | 'blocked';

export type NumberCheck = { ok: true; number: string } | { ok: false; problem: NumberProblem; message: string };

export const NUMBER_MESSAGES: Record<NumberProblem, string> = {
  invalid: 'Enter a 10-digit US or Canada number.',
  emergency: '911 isn’t available yet. Use a mobile or landline.',
  service: 'Service numbers like 411 can’t be called.',
  blocked: 'This number can’t be called.',
};

/** Digits of a number typed or stored: "+1 (209) 348-3263" -> "12093483263"; null if it has letters etc. */
function digitsOf(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  if (!s || /[^0-9+()\-.\s]/.test(s) || s.indexOf('+') > 0) return null;
  return s.replace(/\D/g, '');
}

/** Format only: 1NXXNXXXXXX from "+1 (209) 348-3263", "209-348-3263", "12093483263"; else null. */
export function normalizeNanp(input: unknown): string | null {
  let d = digitsOf(input);
  if (d === null) return null;
  if (d.length === 10) d = `1${d}`;
  return /^1[2-9][0-9]{2}[2-9][0-9]{6}$/.test(d) ? d : null;
}

/** Is a 1NXXNXXXXXX number blocked by the list (or the fixed 555-01XX rule)? */
export function isBlocked(number: string, blocked: readonly string[]): boolean {
  if (/^1[2-9][0-9]{2}55501[0-9]{2}$/.test(number)) return true; // fictional range
  const rest = number.slice(1);
  return blocked.some((p) => rest.startsWith(p));
}

/** May an app user call / forward to / transfer to / add this number? */
export function checkNumber(input: unknown, blocked: readonly string[] = DEFAULT_BLOCKED_PREFIXES): NumberCheck {
  const d = digitsOf(input);
  const fail = (problem: NumberProblem): NumberCheck => ({ ok: false, problem, message: NUMBER_MESSAGES[problem] });
  if (d === null) return fail('invalid');
  // dialled alone: 911, 411, ... (also 1-911)
  if (/^1?911$/.test(d)) return fail('emergency');
  if (/^1?[2-9]11$/.test(d)) return fail('service');
  const n = normalizeNanp(d);
  if (!n) return fail('invalid');
  if (isBlocked(n, blocked)) return fail('blocked');
  return { ok: true, number: n };
}

/** Asterisk global variables for the list: CP_BLK_<prefix>=1 for each, + CP_BLK_READY=1 (the rule refuses calls without it). */
export function blockedGlobals(blocked: readonly string[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const p of blocked) if (PREFIX.test(p)) out.set(`CP_BLK_${p}`, '1');
  out.set('CP_BLK_READY', '1');
  return out;
}
